
import { useState, useCallback, useRef } from 'react';
import { Attendee } from '@/types/attendee';

export const useCheckInButton = (
  attendee: Attendee,
  onSuccess: (attendee: Attendee, timestamp: string) => void,
  onDirectUpdate?: (code: string, updates: any) => Promise<any>,
  onSilentUpdate?: (attendee: Attendee) => void
) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef<boolean>(true);
  
  // Store original t-shirt size
  const originalTShirtSize = attendee.t_shirt_size;
  
  // Safety mechanism to prevent UI freezes - force reset updating state
  useCallback(() => {
    // Safety timeout to reset isUpdating after 5 seconds if it gets stuck
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    updateTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current && isUpdating) {
        console.log('Force resetting isUpdating state after timeout');
        setIsUpdating(false);
      }
    }, 5000);
    
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [isUpdating]);
  
  // Cleanup on unmount
  useCallback(() => {
    return () => {
      isMountedRef.current = false;
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);
  
  const handleCheckInClick = useCallback(async () => {
    if (isUpdating || !attendee) return;
    
    try {
      setIsUpdating(true);
      
      // Create timestamp for the check-in
      const timestamp = new Date().toISOString();
      
      // Prepare update data with t-shirt size preservation
      const updateData = {
        attended: !attendee.attended,
        checkedInAt: !attendee.attended ? timestamp : null,
        t_shirt_size: originalTShirtSize // Preserve t-shirt size
      };
      
      let result = null;
      
      // Use external direct update if provided
      if (onDirectUpdate) {
        result = await onDirectUpdate(attendee.code, updateData);
      }
      
      // If we have a result, update the attendee with it
      if (result) {
        // Merge result with our update data to ensure all fields are present
        const updatedAttendee = {
          ...attendee,
          ...result,
          attended: !attendee.attended,
          checkedInAt: !attendee.attended ? timestamp : null,
          t_shirt_size: result.t_shirt_size || originalTShirtSize
        };
        
        // Call the success callback
        onSuccess(updatedAttendee, timestamp);
        
        // Optionally update UI silently
        if (onSilentUpdate) {
          onSilentUpdate(updatedAttendee);
        }
      }
      
      return result;
    } catch (error) {
      console.error('Error in CheckInButton click handler:', error);
      return null;
    } finally {
      // Safety: ensure we're still mounted before updating state
      if (isMountedRef.current) {
        setIsUpdating(false);
      }
      
      // Clear any pending timeouts
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }
    }
  }, [attendee, isUpdating, onDirectUpdate, onSuccess, onSilentUpdate, originalTShirtSize]);
  
  return {
    isUpdating,
    handleCheckInClick
  };
};
